import QRCode from 'qrcode';
import { LABEL_FONT_STACK } from './labelSizes';

// ─────────────────────────────────────────────────────────────────────────────
// Brother QL-800 ラベルの自動レイアウト設計値（単位 mm）。仕様は
// /app/claude/label-design-spec.md を参照。300dpi 1bit 感熱印字を前提とする。
// 「入らない行は縮小せず落とす」— 縮小して詰め込むと感熱にじみで判読不能になる。
// 例外は識別子のみ: 切り詰めた識別子は「無い」より悪い（別物として読める）ので、
// 幅に収まるまで 4.0→3.4→2.8 と段階を下げ、それでも無理なら印刷を拒否する。
// ─────────────────────────────────────────────────────────────────────────────
// 余白は 2.0mm。QL-800 の紙送り・カット位置の実機ばらつきに対して 1.0mm では端の
// バー/モジュールが欠けるため（実機スキャン失敗の一因）。1.5mm を絶対下限とし、
// これを下回ってはならない。全ロール・全種別・両記号種に共通の共有ジオメトリ。
export const MARGIN_MM = 2.0;
export const MARGIN_FLOOR_MM = 1.5;
// QRはコード枠の中に静粛領域4モジュール（≒1.4mm/辺）を既に含むので、その上に
// 2.0mm を足すと分離を二重取りして 23mm ラベルの1割を空白に使ってしまう。
export const CODE_GAP_MM = 1.0;
export const CODE128_GAP_MM = 2.0;
export const LINE_GAP_MM = 0.8;        // テキスト行間
export const LINE_HEIGHT = 1.05;
export const TYPE_MM = { identity: 4.0, title: 3.4, secondary: 2.8 };
export const IDENTITY_STEPS = [TYPE_MM.identity, TYPE_MM.title, TYPE_MM.secondary];
// ロケーションは棚を通路の反対側から読む用途なので、識別子（ロケーションコード）は
// 余白がある限り大きくする。「大きくする」は縮小ではないので下限規則には反しない。
export const LOCATION_STEPS = [16, 12, 9, 7, 5.5, TYPE_MM.identity, TYPE_MM.title, TYPE_MM.secondary];
export const FLOOR_MM = 2.8;           // 絶対下限。これ未満は描かない
export const JA_FLOOR_MM = 3.4;        // 漢字/かなを含む行の下限（線密度が印字解像度を下回る）
export const CODE_MAX_MM = 25;         // これ以上大きくしても腕の長さのスキャンでは利得なし
export const WIDE_ASPECT = 1.8;        // これ以上の縦横比は「横型」= コード左・テキスト右
export const QR_QUIET_MODULES = 4;     // QR規格が要求する静粛領域（片側4モジュール）

// Code128: 1文字=11モジュール + start/checksum/stop で 35モジュール。静粛領域は
// 片側「細エレメント幅×10」以上。jsbarcode の描画は SVG 内 px なので、必要な
// 幅はモジュール数から算出できる（実測値ベース、推測ではない）。
export const CODE128_NARROW_PX = 1.6;
export const CODE128_QUIET_NARROWS = 10;
export const CODE128_QUIET_PX = CODE128_NARROW_PX * CODE128_QUIET_NARROWS;
export const CODE128_MODULES_PER_CHAR = 11;
export const CODE128_FIXED_MODULES = 35;
// バーコードは「必要な幅」を取る（上限=使えるラベル幅）。0.33mm/モジュールを目標に
// 割り付け、下限 0.25mm を満たせないロールでは Code128 を選べなくする。
export const CODE128_TARGET_NARROW_MM = 0.33;
export const CODE128_MIN_NARROW_MM = 0.25;
// jsbarcode に渡す描画オプションはここが唯一の定義元。SVG固有の高さ（=アスペクト計算）
// もこの値から導出するので、height/fontSize/textMargin を変えても計算がずれない。
export const CODE128_BARCODE_OPTS = {
  format: 'CODE128', displayValue: true, height: 46, fontSize: 15, textMargin: 2,
  width: CODE128_NARROW_PX, margin: 0,
  marginLeft: CODE128_QUIET_PX, marginRight: CODE128_QUIET_PX,
};
const code128IntrinsicHPx = (showText) => CODE128_BARCODE_OPTS.height
  + (showText ? CODE128_BARCODE_OPTS.fontSize + CODE128_BARCODE_OPTS.textMargin : 0);
const CODE128_INTRINSIC_H_PX = code128IntrinsicHPx(true);

// スキャン可能な最小モジュールサイズ。実機スキャン試験の結果で置き換える前提の暫定値。
// 変更は必ずこの1箇所だけ。
export const QR_MIN_MODULE_MM = 0.33;

const JA_RE = /[\u3000-\u303f\u3040-\u309f\u30a0-\u30ff\u3400-\u4dbf\u4e00-\u9fff\uff00-\uffef]/;
export const hasJapanese = (s) => JA_RE.test(String(s || ''));

/** 静粛領域込みのモジュール数。ペイロードから実測する（推定値は使わない）。 */
export function qrTotalModules(value) {
  try {
    return QRCode.create(String(value || ''), { errorCorrectionLevel: 'M' }).modules.size + QR_QUIET_MODULES * 2;
  } catch { return null; }
}

/** Code128 の静粛領域込みモジュール数。CODE128 は自動でサブセット圧縮するため
 *  これは上限側の見積り（=幅を広めに取る安全側）。 */
export const code128TotalModules = (value) => String(value || '').length * CODE128_MODULES_PER_CHAR
  + CODE128_FIXED_MODULES + CODE128_QUIET_NARROWS * 2;

// ── 文字幅 ───────────────────────────────────────────────────────────────────
// ブラウザでは canvas で実測する（ラベルと実際に同じ書体スタックで）。書体が
// プレビュー/印刷とずれると、識別子が幅計算をすり抜けて無言で切れるため、
// 書体は labelSizes.LABEL_FONT_STACK が唯一の定義元。
// jsdom（単体テスト）では canvas が無いので、全角1.0em / 半角0.6em の
// 安全側見積りにフォールバックする。
const LABEL_FONT = LABEL_FONT_STACK;
let ctx2d;
const measured = (text, sizeMm, bold) => {
  try {
    if (ctx2d === undefined) ctx2d = document.createElement('canvas').getContext('2d') || null;
    if (!ctx2d) return null;
    ctx2d.font = `${bold ? '700 ' : ''}100px ${LABEL_FONT}`;
    const w = ctx2d.measureText(String(text)).width;
    return w ? (w / 100) * sizeMm : null;
  } catch { return null; }
};
const estimated = (text, sizeMm, bold) => [...String(text)]
  .reduce((a, c) => a + (JA_RE.test(c) ? 1.0 : 0.6), 0) * sizeMm * (bold ? 1.05 : 1);

export const textWidthMm = (text, sizeMm, bold = false) => (
  measured(text, sizeMm, bold) ?? estimated(text, sizeMm, bold));

const lineMm = (sizeMm) => sizeMm * LINE_HEIGHT;
const sizeFor = (base, text) => Math.max(base, hasJapanese(text) ? JA_FLOOR_MM : FLOOR_MM);

// 行の優先順位 = 識別子 > 品名 > その他の項目（データ側の並び順）。
// 識別子はラベルデータの先頭項目。接頭辞を付けない（「設備番号:」を付けない）。
// promoteIdentity=false（Code128）のときは、識別子はバーコードの文字列表示が
// 兼ねるので行にしない（同じ値を2回刷らない）。
function candidateLines(data, promoteIdentity = true) {
  // ロケーション: 識別子=ロケーションコード（大きく）→ 名称 → 補足（部門/倉庫）。
  // Code128 でもバーコードの文字列表示は出さず（showCodeText=false）、代わりに
  // このコード行を大きく刷る。識別子は1ラベル1回のまま、遠くから読める。
  if (data.type === 'location') {
    const code = String(data.barcode ?? '');
    return [
      { kind: 'identity', key: 'ロケーション', text: code, bold: true, sizeMm: sizeFor(TYPE_MM.identity, code) },
      data.big ? { kind: 'title', key: '名称', text: String(data.big), bold: false, sizeMm: sizeFor(TYPE_MM.title, data.big) } : null,
      data.subtitle ? { kind: 'secondary', key: '補足', text: String(data.subtitle), bold: false, sizeMm: sizeFor(TYPE_MM.secondary, data.subtitle) } : null,
    ].filter((l) => l && l.text.trim() !== '');
  }

  const fields = data.fields || [];
  const out = [];
  const rest = [];
  if (fields[0]) {
    if (promoteIdentity) {
      out.push({ kind: 'identity', key: fields[0].k || '識別子', text: String(fields[0].v ?? ''), bold: true,
        sizeMm: sizeFor(TYPE_MM.identity, fields[0].v) });
      rest.push(...fields.slice(1));
    } else {
      // Phase 3（構造規則）: バーコードがあるなら、その文字列表示が識別子行そのもの。
      // よって先頭項目＝識別子は行として出さない。値の比較はしない — 前ゼロ・大小文字・
      // 空白の差で一致判定が外れると、重複が黙って復活するため。
      rest.push(...(data.barcode ? fields.slice(1) : fields));
    }
  }
  if (data.title) {
    out.push({ kind: 'title', key: '品名', text: String(data.title), bold: false,
      sizeMm: sizeFor(TYPE_MM.title, data.title) });
  }
  rest.forEach((f) => {
    const text = `${f.k}: ${f.v}`;
    out.push({ kind: 'secondary', key: f.k, text, bold: false, sizeMm: sizeFor(TYPE_MM.secondary, text) });
  });
  return out.filter((l) => l.text.trim() !== '' && !/:\s*$/.test(l.text));
}

/** 幅に収まる識別子サイズを返す。段階を下げても収まらなければ null（=印刷不可）。 */
function identitySizeFor(line, availWidthMm, steps = IDENTITY_STEPS) {
  const floor = hasJapanese(line.text) ? JA_FLOOR_MM : FLOOR_MM;
  for (const s of steps) {
    if (s < floor) continue;
    if (textWidthMm(line.text, s, true) <= availWidthMm + 1e-6) return s;
  }
  return null;
}

const stackHeight = (list) => list.reduce((a, l, i) => a + (i ? LINE_GAP_MM : 0) + lineMm(l.sizeMm), 0);

/**
 * ロケーションの識別子行を、他の行も残せる範囲で最大まで大きくする。
 * 他の行を残せる段階が無ければ、単独で収まる最大段階まで戻す（識別子が最優先）。
 */
function growLocationIdentity(lines, availWidthMm, availHeightMm) {
  if (!lines[0] || lines[0].kind !== 'identity') return lines;
  const others = lines.slice(1);
  const fits = (size, withOthers) => {
    const first = { ...lines[0], sizeMm: size };
    return textWidthMm(first.text, size, true) <= availWidthMm + 1e-6
      && stackHeight(withOthers ? [first, ...others] : [first]) <= availHeightMm + 1e-6;
  };
  const size = LOCATION_STEPS.find((s) => fits(s, true))
    ?? LOCATION_STEPS.find((s) => fits(s, false));
  return size == null ? lines : [{ ...lines[0], sizeMm: Math.max(size, lines[0].sizeMm) }, ...others];
}

/** 縦・横の両方で入る行だけを残す。横があふれる行は省略記号ではなく「落とす」。 */
function fitLines(lines, availHMm, availWMm) {
  const kept = []; const dropped = [];
  let used = 0;
  lines.forEach((l) => {
    const needH = (kept.length === 0 ? 0 : LINE_GAP_MM) + lineMm(l.sizeMm);
    const fitsH = used + needH <= availHMm + 1e-6;
    const fitsW = availWMm >= 8 && textWidthMm(l.text, l.sizeMm, l.bold) <= availWMm + 1e-6;
    if (fitsH && fitsW) { kept.push(l); used += needH; } else { dropped.push(l); }
  });
  return { kept, dropped };
}

const droppedLabels = (lines) => lines.map((l) => (l.kind === 'title' ? '品名' : l.key));

/**
 * QRラベルの割り付け。適用外（Code128 / ロケーション / 長尺ロール）は null を返す。
 * 手順: ①保証する識別子1行分を先に確保 ②残り全部をコードに（25mm上限）
 *       ③モジュールサイズが下限を満たすか確認 ④満たすなら確定
 *       ⑤満たさないなら下限に届くまで下位の行から落とす（落とした事実は明示）
 */
export function planQrLabel({ data, w, h, format }) {
  if (format !== 'qr' || !h) return null;

  const usableW = Math.max(w - MARGIN_MM * 2, 4);
  const usableH = Math.max(h - MARGIN_MM * 2, 4);
  const layout = w / h >= WIDE_ASPECT ? 'wide' : 'stacked';
  const lines = candidateLines(data);
  // モジュール数が取れないとき（QR生成失敗）は「不明」として扱い、コードを最大にして
  // ダイアログで警告する。既定値でごまかすと読めないラベルを黙って印刷してしまう。
  const total = qrTotalModules(data.barcode);
  const need = total == null ? Infinity : total * QR_MIN_MODULE_MM;
  const identityMm = lines[0]?.kind === 'identity' ? lineMm(lines[0].sizeMm) : 0;

  const codeCeil = layout === 'wide' ? usableH : Math.min(usableW, usableH);
  const ideal = layout === 'wide'
    ? Math.min(usableH, CODE_MAX_MM)
    : Math.min(usableW, Math.max(usableH - identityMm - CODE_GAP_MM, 0), CODE_MAX_MM);
  // スキャン性はテキストより上位 — 下限に届くまでは 25mm 上限を超えてでもコードを優先する。
  const codeMm = Math.max(ideal, Math.min(need, codeCeil));
  const moduleMm = total == null ? null : codeMm / total;

  const textWidth = layout === 'wide' ? usableW - codeMm - CODE_GAP_MM : usableW;
  const availText = layout === 'wide' ? usableH : Math.max(usableH - codeMm - CODE_GAP_MM, 0);

  // 識別子は幅に合わせて段階を下げる（切り詰めない）。下げても無理なら印刷拒否。
  let blocked = data.identityMissing || null;
  if (!blocked && lines[0]?.kind === 'identity') {
    const s = identitySizeFor(lines[0], textWidth);
    if (s == null) {
      blocked = { field: lines[0].key,
        message: `${lines[0].key}「${lines[0].text}」がラベル幅に収まりません（${FLOOR_MM}mmまで下げても不可）。大きいロールを選んでください。` };
    } else { lines[0] = { ...lines[0], sizeMm: s }; }
  }

  let lines2 = lines;
  if (data.type === 'location') lines2 = growLocationIdentity(lines, textWidth, availText);
  const { kept, dropped } = fitLines(lines2, availText, textWidth);

  return {
    format: 'qr', layout, codeMm, moduleMm, totalModules: total, minModuleMm: QR_MIN_MODULE_MM,
    tooLong: moduleMm == null || moduleMm < QR_MIN_MODULE_MM - 1e-9,
    blocked,
    lines: kept, dropped: droppedLabels(dropped),
    textWidthMm: textWidth, marginMm: MARGIN_MM, codeGapMm: CODE_GAP_MM, lineGapMm: LINE_GAP_MM,
  };
}

/**
 * Code128ラベルの割り付け（Phase 2/3）。バーコードは幅を必要とするため上段・
 * 必要な幅（上限=ラベル幅）で、テキストは下段。整列はラベル内で1種類（左寄せ）。
 * 識別子はバーコードの文字列表示が担うので項目行にしない（Phase 3）。
 */
export function planCode128Label({ data, w, h, format }) {
  if (format !== 'code128' || !h) return null;

  const usableW = Math.max(w - MARGIN_MM * 2, 4);
  const usableH = Math.max(h - MARGIN_MM * 2, 4);

  // ロケーションはコードを大きな文字行として刷るので、バーコード下の文字列表示は出さない
  // （同じ値を2回刷らない）。文字が無い分アスペクトが変わるのでここで反映する。
  const showCodeText = data.type !== 'location';
  const narrows = code128TotalModules(data.barcode);
  const aspect = code128IntrinsicHPx(showCodeText) / (narrows * CODE128_NARROW_PX);  // 描画SVGの縦/横
  let codeW = Math.min(usableW, narrows * CODE128_TARGET_NARROW_MM);
  const maxCodeH = Math.max(usableH * 0.5, 6);
  let codeMm = codeW * aspect;
  if (codeMm > maxCodeH) { codeMm = maxCodeH; codeW = codeMm / aspect; }
  const narrowMm = codeW / narrows;

  // ロケーションはコード行を昇格させる（バーコードの文字列表示を出さないため）。
  const lines = candidateLines(data, !showCodeText);
  const availText = Math.max(usableH - codeMm - CODE128_GAP_MM, 0);
  const grown = data.type === 'location' ? growLocationIdentity(lines, usableW, availText) : lines;
  const { kept, dropped } = fitLines(grown, availText, usableW);

  return {
    format: 'code128', layout: 'strip', codeMm, codeWidthMm: codeW, showCodeText,
    narrowMm, minNarrowMm: CODE128_MIN_NARROW_MM, totalModules: narrows,
    quietZoneNarrows: CODE128_QUIET_NARROWS, narrowPx: CODE128_NARROW_PX,
    // 下限を満たせないロールでは Code128 自体を選べなくする（黙ってQRに変えない）。
    tooNarrow: narrowMm < CODE128_MIN_NARROW_MM - 1e-9,
    blocked: data.identityMissing || null,
    lines: kept, dropped: droppedLabels(dropped),
    textWidthMm: usableW, marginMm: MARGIN_MM, codeGapMm: CODE128_GAP_MM, lineGapMm: LINE_GAP_MM,
  };
}

/** どちらの経路でも同じ入口で計画を取れるようにする（ダイアログ・テストシート用）。 */
export const planLabel = (args) => planQrLabel(args) || planCode128Label(args);

/**
 * このロール・このデータで Code128 が細バー下限を満たせるか。
 * 自動レイアウト経路は planCode128Label と同じ割り付け、ロケーション/長尺ロールは
 * 従来レイアウト（高さでSVGを拡縮）の実寸から逆算する:
 *   細バー = コード高さ × 細エレメントpx ÷ SVG固有高さpx
 * レイアウトを変えるまでの間も「下限未満のまま刷らせない」ためにゲートだけ先に効かせる。
 */
export function code128Feasibility({ data, w, h }) {
  const narrows = code128TotalModules(data?.barcode);
  const neededWidthMm = narrows * CODE128_MIN_NARROW_MM;
  const legacy = !h;   // 長尺ロールのみ従来レイアウト（ロケーションは自動レイアウトに移行済み）
  let narrowMm;
  if (legacy) {
    const usableH = h ? Math.max(h - 2.4, 6) : null;
    const codeMm = usableH ? Math.min(Math.max(usableH * 0.36, 7), 14) : 13;
    narrowMm = codeMm * CODE128_NARROW_PX / CODE128_INTRINSIC_H_PX;
  } else {
    narrowMm = planCode128Label({ data, w, h, format: 'code128' }).narrowMm;
  }
  return {
    narrowMm, neededWidthMm, totalModules: narrows,
    usableWidthMm: Math.max(w - MARGIN_MM * 2, 4),
    minNarrowMm: CODE128_MIN_NARROW_MM,
    tooNarrow: narrowMm < CODE128_MIN_NARROW_MM - 1e-9,
    legacyLayout: legacy,
  };
}
